import { inject } from 'inversify';
import { APP_TIMEZONE } from '../../config/timezone';
import { Injectable } from '../../decorators/injectable';
import { getHttpErrorBody, getHttpErrorStatus } from '../../utils/error.utils';
import { HttpService, type IHttpService } from '../http/http.service';
import { type IKyClientService, KyClientService } from '../ky-client/ky-client.service';

/**
 * Machine code del gate T0.4: il backend risponde 422 con
 * `{ code: 'nc_document_signature_required' }` quando si collega a una NC un
 * documento PENDING senza dichiarare `alreadySigned`.
 */
export const NC_DOCUMENT_SIGNATURE_REQUIRED_CODE = 'nc_document_signature_required';

/** True quando l'errore è il 422 "firma richiesta per chiudere la NC". */
export function isNcSignatureRequiredError(err: unknown): boolean {
  if (getHttpErrorStatus(err) !== 422) {
    return false;
  }
  const body = getHttpErrorBody(err) as { code?: unknown } | null | undefined;
  return !!body && typeof body === 'object' && body.code === NC_DOCUMENT_SIGNATURE_REQUIRED_CODE;
}

export type NonConformitySourceType =
  | 'TEMPERATURE'
  | 'CLEANING'
  | 'OIL'
  | 'PEST_CONTROL'
  | 'METAL_DETECTION'
  | 'BLAST_CHILLING'
  | 'COOKING_TEMPERATURE';

export interface NonConformityDocumentResponse {
  id: string;
  sourceType: NonConformitySourceType;
  sourceId: string;
  signedDocumentId: string;
  recordDate: string;
  signedDocument: {
    id: string;
    originalFilename: string;
    category: string;
    createdAt: string;
    signedStorageKey: string | null;
    /**
     * Stato di firma del documento collegato: il backend lo espone SEMPRE
     * (ref arricchito con lo status corrente, vedi `withDocumentStatus`
     * lato BE) — la UI mostra il badge firma e concede la rimozione solo
     * per i PENDING.
     */
    status: 'PENDING' | 'SIGNED';
  };
  createdAt: string;
}

/**
 * Risposta di `replace`/`replace-with-existing`: stesso shape dell'upload/
 * attach più `replacedSignedDocumentId`, l'id del documento sostituito (che
 * resta nell'archivio, solo scollegato da questa NC).
 */
export interface NonConformityDocumentReplaceResponse extends NonConformityDocumentResponse {
  replacedSignedDocumentId: string;
}

/**
 * Fase 3 part A ("ricontrollo") — stato derivato del ricontrollo dell'azione
 * correttiva, mai persistito sul verbale: `dueDate` presente solo per
 * SCHEDULED/DUE_TODAY/OVERDUE, `waivedReason` solo per WAIVED.
 */
export type NcRecheckStatus =
  | 'NOT_SCHEDULED'
  | 'WAIVED'
  | 'SCHEDULED'
  | 'DUE_TODAY'
  | 'OVERDUE'
  | 'DONE';

export interface NcRecheckStatusResponse {
  status: NcRecheckStatus;
  dueDate?: string;
  waivedReason?: string;
}

interface AttachDocumentRequest {
  signedDocumentId: string;
  sourceType: NonConformitySourceType;
  sourceId: string;
  recordDate: string;
  /**
   * Dichiarazione "già firmato su carta" (T0.4): flip PENDING→SIGNED lato
   * backend. Assente per i documenti già SIGNED (attach normale).
   */
  alreadySigned?: boolean;
}

export interface UploadAndAttachOptions {
  /**
   * `true` = il PDF è già firmato su carta (doc SIGNED subito);
   * assente/false = doc PENDING, la NC resta aperta finché non viene firmato.
   */
  alreadySigned?: boolean;
}

interface ReplaceWithExistingRequest {
  signedDocumentId: string;
  sourceType: NonConformitySourceType;
  sourceId: string;
  recordDate: string;
  /**
   * `true` = dichiarazione "già firmato su carta"; assente/`false` = il
   * backend applica lui la firma grafica (stessa posizione di default del
   * flusso di primo upload) prima di sostituire il documento.
   */
  alreadySigned?: boolean;
}

export interface INonConformityDocumentService {
  findBySource(
    sourceType: NonConformitySourceType,
    sourceId: string,
  ): Promise<NonConformityDocumentResponse[]>;
  findOpenDays(sourceType: NonConformitySourceType, from: string, to: string): Promise<string[]>;
  /** Fase 3 part A ("ricontrollo"): stato derivato del ricontrollo per questa fonte. */
  findRecheckStatus(
    sourceType: NonConformitySourceType,
    sourceId: string,
  ): Promise<NcRecheckStatusResponse>;
  uploadAndAttach(
    sourceType: NonConformitySourceType,
    sourceId: string,
    recordDate: string,
    file: File,
    options?: UploadAndAttachOptions,
  ): Promise<NonConformityDocumentResponse>;
  attach(data: AttachDocumentRequest): Promise<NonConformityDocumentResponse>;
  detach(id: string): Promise<void>;
  /**
   * Sostituisce il documento di non conformità già collegato con un nuovo
   * upload. A differenza di `uploadAndAttach`, la firma (grafica o
   * dichiarazione) è applicata dal backend nella stessa chiamata: nessuna
   * seconda richiesta di firma lato client.
   */
  replaceWithUpload(
    sourceType: NonConformitySourceType,
    sourceId: string,
    recordDate: string,
    file: File,
    options?: UploadAndAttachOptions,
  ): Promise<NonConformityDocumentReplaceResponse>;
  /** Sostituisce il documento già collegato con uno scelto dall'archivio. */
  replaceWithExisting(
    data: ReplaceWithExistingRequest,
  ): Promise<NonConformityDocumentReplaceResponse>;
}

@Injectable()
class NonConformityDocumentService implements INonConformityDocumentService {
  constructor(
    @inject(HttpService) private readonly httpService: IHttpService,
    @inject(KyClientService) private readonly kyClientService: IKyClientService,
  ) {}

  async findBySource(
    sourceType: NonConformitySourceType,
    sourceId: string,
  ): Promise<NonConformityDocumentResponse[]> {
    return this.httpService.get<NonConformityDocumentResponse[]>(
      `non-conformity-documents?sourceType=${sourceType}&sourceId=${sourceId}`,
    );
  }

  async findRecheckStatus(
    sourceType: NonConformitySourceType,
    sourceId: string,
  ): Promise<NcRecheckStatusResponse> {
    return this.httpService.get<NcRecheckStatusResponse>(
      `non-conformity-follow-ups?sourceType=${sourceType}&sourceId=${sourceId}`,
    );
  }

  async findOpenDays(
    sourceType: NonConformitySourceType,
    from: string,
    to: string,
  ): Promise<string[]> {
    return this.httpService.get<string[]>(
      `non-conformity-documents/open-days?sourceType=${sourceType}&from=${from}&to=${to}&tz=${encodeURIComponent(APP_TIMEZONE)}`,
    );
  }

  async uploadAndAttach(
    sourceType: NonConformitySourceType,
    sourceId: string,
    recordDate: string,
    file: File,
    options?: UploadAndAttachOptions,
  ): Promise<NonConformityDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({
      sourceType,
      sourceId,
      recordDate,
    });
    if (options?.alreadySigned) {
      // Query string 'true' (contratto T0.4: il controller legge la stringa).
      params.set('alreadySigned', 'true');
    }

    const client = this.kyClientService.getClient();
    return client
      .post(`non-conformity-documents/upload?${params.toString()}`, { body: formData })
      .json<NonConformityDocumentResponse>();
  }

  async attach(data: AttachDocumentRequest): Promise<NonConformityDocumentResponse> {
    return this.httpService.post<NonConformityDocumentResponse>(
      'non-conformity-documents/attach',
      data,
    );
  }

  async detach(id: string): Promise<void> {
    await this.httpService.delete(`non-conformity-documents/${id}`);
  }

  async replaceWithUpload(
    sourceType: NonConformitySourceType,
    sourceId: string,
    recordDate: string,
    file: File,
    options?: UploadAndAttachOptions,
  ): Promise<NonConformityDocumentReplaceResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sourceType', sourceType);
    formData.append('sourceId', sourceId);
    formData.append('recordDate', recordDate);
    if (options?.alreadySigned !== undefined) {
      formData.append('alreadySigned', options.alreadySigned ? 'true' : 'false');
    }

    const client = this.kyClientService.getClient();
    return client
      .post('non-conformity-documents/replace', { body: formData })
      .json<NonConformityDocumentReplaceResponse>();
  }

  async replaceWithExisting(
    data: ReplaceWithExistingRequest,
  ): Promise<NonConformityDocumentReplaceResponse> {
    return this.httpService.post<NonConformityDocumentReplaceResponse>(
      'non-conformity-documents/replace-with-existing',
      data,
    );
  }
}

export { NonConformityDocumentService };
